import { useState } from 'react';
import type { Playlist } from '@blackblack/shared';
import { formatDuration } from '@blackblack/shared';
import { api, errorMessage } from '../../lib/api';
import { invalidate, setQueryData, useQuery } from '../../lib/query';
import { Link, navigate, useParams } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { player } from '../../state/player';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { Avatar, Button, EmptyState, ErrorState, IconButton, Skeleton } from '../../components/ui/primitives';
import { ActionList, Sheet, useOpenSheet } from '../../components/ui/Sheet';
import { Page, PageHeader } from '../../components/layout';
import { Cover } from '../music/Cover';
import { TrackRow } from '../music/TrackRow';
import { ReportSheet } from '../moderation/ReportSheet';
import { PlaylistEditorSheet } from './PlaylistEditorSheet';
import styles from './playlists.module.css';

export function PlaylistPage() {
  const { id } = useParams<{ id: string }>();
  const key = `playlist:${id}`;
  const q = useQuery<Playlist>(key, () => api.get(`/playlists/${id}`));
  const { requireAuth } = useAuth();
  const toast = useToast();
  const openSheet = useOpenSheet();
  const [reordering, setReordering] = useState(false);
  const p = q.data;

  const mutate = async (fn: () => Promise<Playlist>, msg?: string) => {
    try {
      const next = await fn();
      setQueryData<Playlist>(key, () => next);
      invalidate('playlists:');
      if (msg) toast(msg);
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
    }
  };

  const move = (from: number, to: number) => {
    if (!p || to < 0 || to >= p.tracks.length) return;
    const ids = p.tracks.map((t) => t.track.id);
    const [m] = ids.splice(from, 1);
    ids.splice(to, 0, m!);
    // optimistic reorder
    const reordered = ids.map((tid, i) => ({ ...p.tracks.find((t) => t.track.id === tid)!, position: i }));
    setQueryData<Playlist>(key, (prev) => (prev ? { ...prev, tracks: reordered } : prev));
    void mutate(() => api.put<Playlist>(`/playlists/${p.id}/order`, { trackIds: ids }));
  };

  const share = async () => {
    const url = `${location.origin}/playlist/${id}`;
    try {
      if (navigator.share) await navigator.share({ title: p?.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast('Link gekopieerd');
      }
    } catch {
      /* cancelled */
    }
  };

  const openMenu = () =>
    p &&
    openSheet((close) => (
      <Sheet open onClose={close} title={p.title}>
        <ActionList
          actions={[
            { label: 'Bewerken', icon: <Icon name="edit" size={20} />, hidden: !p.isOwner, onClick: () => (close(), openSheet((c) => <PlaylistEditorSheet playlist={p} onClose={c} onSaved={(n) => setQueryData(key, () => n)} />)) },
            { label: reordering ? 'Klaar met volgorde' : 'Volgorde wijzigen', icon: <Icon name="queue" size={20} />, hidden: !p.canEdit || p.tracks.length < 2, onClick: () => (close(), setReordering((r) => !r)) },
            { label: p.isPublic ? 'Privé maken' : 'Openbaar maken', icon: <Icon name={p.isPublic ? 'lock' : 'globe'} size={20} />, hidden: !p.isOwner, onClick: () => (close(), void mutate(() => api.patch(`/playlists/${p.id}`, { isPublic: !p.isPublic }), p.isPublic ? 'Playlist is nu privé' : 'Playlist is nu openbaar')) },
            { label: 'Delen', icon: <Icon name="share" size={20} />, hidden: !p.isPublic, onClick: () => (close(), void share()) },
            { label: 'Rapporteren', icon: <Icon name="flag" size={20} />, hidden: p.isOwner, danger: true, onClick: () => (close(), requireAuth() && openSheet((c) => <ReportSheet targetType="playlist" targetId={p.id} onClose={c} />)) },
            {
              label: 'Playlist verwijderen',
              icon: <Icon name="trash" size={20} />,
              hidden: !p.isOwner,
              danger: true,
              onClick: async () => {
                close();
                if (!confirm(`"${p.title}" verwijderen?`)) return;
                try {
                  await api.delete(`/playlists/${p.id}`);
                  invalidate('playlists:');
                  toast('Playlist verwijderd');
                  navigate('/music', { replace: true });
                } catch (e) {
                  toast(errorMessage(e), { tone: 'error' });
                }
              },
            },
          ]}
        />
      </Sheet>
    ));

  if (q.error) {
    return (
      <Page>
        <PageHeader fallback="/music" />
        <ErrorState error={q.error} onRetry={q.refetch} />
      </Page>
    );
  }

  const total = p?.tracks.reduce((s, t) => s + t.track.durationSec, 0) ?? 0;
  const playAll = (shuffle = false) => {
    if (!p) return;
    if (shuffle !== player.state.shuffle) player.toggleShuffle();
    player.playTracks(
      p.tracks.map((t) => t.track),
      shuffle ? Math.floor(Math.random() * p.tracks.length) : 0,
      p.title,
    );
    void api.post(`/playlists/${p.id}/play`).catch(() => {});
  };

  return (
    <Page>
      <PageHeader fallback="/music" actions={p && <IconButton icon="more" label="Meer" onClick={openMenu} />} />
      <div className={styles.hero}>
        {p ? <Cover src={p.coverUrl} size={200} radius={16} /> : <Skeleton w={200} h={200} r={16} />}
        <h1>{p?.title ?? <Skeleton w={180} h={28} />}</h1>
        {p?.description && <p className={styles.desc}>{p.description}</p>}
        {p && (
          <div className={styles.owner}>
            <Link to={`/u/${p.owner.username}`}>
              <Avatar user={p.owner} size={22} /> {p.owner.displayName}
            </Link>
            <span>
              {!p.isPublic && (
                <>
                  <Icon name="lock" size={12} /> Privé ·{' '}
                </>
              )}
              {p.tracks.length} nummers · {formatDuration(total)}
              {p.followerCount > 0 && ` · ${p.followerCount} volgers`}
            </span>
          </div>
        )}
        {p && (
          <div className={styles.actions}>
            {!p.isOwner && (
              <Button
                variant={p.following ? 'outline' : 'secondary'}
                size="md"
                onClick={() =>
                  requireAuth('Log in om playlists te volgen') &&
                  void (p.following ? api.delete(`/playlists/${p.id}/follow`) : api.post(`/playlists/${p.id}/follow`))
                    .then(() => {
                      setQueryData<Playlist>(key, (prev) => (prev ? { ...prev, following: !prev.following, followerCount: prev.followerCount + (prev.following ? -1 : 1) } : prev));
                      invalidate('playlists:followed');
                    })
                    .catch((e: unknown) => toast(errorMessage(e), { tone: 'error' }))
                }
              >
                {p.following ? 'Volgend' : 'Volgen'}
              </Button>
            )}
            <IconButton icon="shuffle" label="Shuffle" onClick={() => playAll(true)} />
            <button className={styles.playBig} onClick={() => playAll()} disabled={!p.tracks.length} aria-label="Afspelen">
              <Icon name="play" size={26} />
            </button>
          </div>
        )}
      </div>

      {reordering && (
        <div className={styles.reorderBar}>
          <span>Gebruik de pijltjes om de volgorde te wijzigen</span>
          <Button size="sm" onClick={() => setReordering(false)}>
            Klaar
          </Button>
        </div>
      )}

      <div className={styles.tracks}>
        {p && p.tracks.length === 0 && (
          <EmptyState icon="note" title="Deze playlist is nog leeg" text={p.canEdit ? 'Voeg nummers toe via het ⋯-menu bij een nummer of vanaf een geluidspagina.' : undefined} action={p.canEdit && <Button onClick={() => navigate('/search?type=tracks')}>Nummers zoeken</Button>} />
        )}
        {p?.tracks.map((t, i) => (
          <TrackRow
            key={t.track.id}
            track={t.track}
            list={p.tracks.map((x) => x.track)}
            index={i}
            context={p.title}
            trailing={
              reordering ? (
                <span className={styles.reorder}>
                  <IconButton icon="up" label="Omhoog" size={18} disabled={i === 0} onClick={() => move(i, i - 1)} />
                  <IconButton icon="down" label="Omlaag" size={18} disabled={i === p.tracks.length - 1} onClick={() => move(i, i + 1)} />
                </span>
              ) : undefined
            }
            extraActions={p.canEdit ? [{ label: 'Verwijderen uit deze playlist', icon: <Icon name="trash" size={20} />, danger: true, onClick: () => void mutate(() => api.delete(`/playlists/${p.id}/tracks/${t.track.id}`), 'Verwijderd uit playlist') }] : []}
          />
        ))}
      </div>
    </Page>
  );
}
